import { MetricValues } from "calculation/metric";
import { useEffect } from "react";
import { ReactElement } from "react";
import { ParameterValues } from "calculation/parameter";
import { Action, State } from "../../state/state";
import { calculate } from "calculation/calculate";
import { CalculationTable } from "./CalculationTable";
import { ReportLinks } from "components/zoning-plan-common/ReportLinks";
import { CalculationHead } from "./CalculationHead";
import { NoData } from "components/common/NoData";

export function Calculation(props: {
  dispatch: (action: Action) => void;
  state: State;
}): ReactElement {
  const dispatch = props.dispatch;
  const state = props.state;

  useEffect(() => {
    async function calculateCapacity() {
      if (
        state.metrics !== undefined &&
        state.selectedMunicipality !== undefined &&
        state.zoningPlanDraft.selectedScenarioLeft !== undefined &&
        state.zoningPlanDraft.selectedParameterSetLeft !== undefined
      ) {
        if (state.zoningPlanDraft.calculationStateLeft === undefined) {
          const metricValuesLeft = new MetricValues(
            state.zoningPlanDraft.selectedScenarioLeft.metricValues
          );

          const parameterValuesLeft = new ParameterValues(
            state.zoningPlanDraft.selectedParameterSetLeft.parameterValues
          );

          const calculationStateLeft = await calculate(
            state.metrics,
            metricValuesLeft,
            parameterValuesLeft,
            state.zoningPlanDraft.selectedScenarioLeft
          );

          dispatch({
            type: "setCalculationStateLeft",
            data: {
              calculationState: calculationStateLeft,
            },
          });
        }

        if (
          state.zoningPlanDraft.selectedScenarioRight !== undefined &&
          state.zoningPlanDraft.selectedParameterSetRight !== undefined &&
          state.zoningPlanDraft.calculationStateRight === undefined
        ) {
          const metricValuesRight = new MetricValues(
            state.zoningPlanDraft.selectedScenarioRight.metricValues
          );

          const parameterValuesRight = new ParameterValues(
            state.zoningPlanDraft.selectedParameterSetRight.parameterValues
          );

          const calculationStateRight = await calculate(
            state.metrics,
            metricValuesRight,
            parameterValuesRight,
            state.zoningPlanDraft.selectedScenarioRight
          );

          dispatch({
            type: "setCalculationStateRight",
            data: {
              calculationState: calculationStateRight,
            },
          });
        }
      }
    }
    calculateCapacity();
  }, [
    dispatch,
    state.selectedMunicipality,
    state.metrics,
    state.parameters,
    state.zoningPlanDraft.selectedScenarioLeft,
    state.zoningPlanDraft.selectedScenarioRight,
    state.zoningPlanDraft.selectedParameterSetLeft,
    state.zoningPlanDraft.selectedParameterSetRight,
    state.zoningPlanDraft.calculationStateLeft,
    state.zoningPlanDraft.calculationStateRight,
  ]);

  const hasData =
    state.metrics !== undefined &&
    state.selectedMunicipality !== undefined &&
    state.zoningPlanDraft.scenarioNames !== undefined &&
    state.zoningPlanDraft.scenarioNames.length > 0;

  return (
    <div className="flex flex-col text-gray-700 max-w-full">
      {!hasData && <NoData />}
      {hasData && (
        <>
          <h1 className="text-lg font-bold mb-2">
            Bauzonenkapazitäten vergleichen
          </h1>
          <p className="my-2">
            Sie können hier die Bauzonenkapazitäten zweier Zonenpläne berechnen
            und vergleichen lassen. Sie können auch nur einen Zonenplan mit
            Kennzahlen selektieren, um die Kapazitätskennzahlen berechnen zu
            lassen.
          </p>
          <div className="grid grid-cols-4 gap-y-4 gap-x-4 bg-gray-50 p-4">
            <CalculationHead dispatch={dispatch} state={state} />
          </div>
          <div className="p-4 grid grid-cols-4 gap-y-4 gap-x-4 bg-gray-100">
            <CalculationTable state={state} />
          </div>

          <div className="grid grid-cols-4 bg-gray-100 gap-y-4 gap-x-4 p-4">
            {state.zoningPlanDraft.selectedScenarioLeft !== undefined &&
              state.zoningPlanDraft.selectedParameterSetLeft !== undefined &&
              state.zoningPlanDraft.calculationStateLeft !== undefined && (
                <div className="col-start-2 p-4">
                  <h2 className="font-bold">Downloads</h2>
                  <ReportLinks
                    metrics={state.metrics!}
                    calculationState={
                      state.zoningPlanDraft.calculationStateLeft
                    }
                    municipality={state.selectedMunicipality!}
                    scenario={state.zoningPlanDraft.selectedScenarioLeft}
                    parameterSetModifiedAt={
                      state.zoningPlanDraft.selectedParameterSetLeft.modifiedAt
                    }
                  />
                </div>
              )}
            {state.zoningPlanDraft.selectedScenarioRight !== undefined &&
              state.zoningPlanDraft.selectedParameterSetRight !== undefined &&
              state.zoningPlanDraft.calculationStateRight !== undefined && (
                <div className="col-start-3 p-4">
                  <h2 className="font-bold">Downloads</h2>
                  <ReportLinks
                    metrics={state.metrics!}
                    calculationState={
                      state.zoningPlanDraft.calculationStateRight
                    }
                    municipality={state.selectedMunicipality!}
                    scenario={state.zoningPlanDraft.selectedScenarioRight}
                    parameterSetModifiedAt={
                      state.zoningPlanDraft.selectedParameterSetRight.modifiedAt
                    }
                  />
                </div>
              )}
          </div>
          {state.zoningPlanDraft.calculationStateLeft !== undefined &&
            state.zoningPlanDraft.calculationStateLeft.metricCalculationResultMessages
              .filter((m) => m.severity === "error")
              .map((m) => <div>{m.message}</div>)}
        </>
      )}
    </div>
  );
}
